import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCustomer } from '../hooks/useCustomers';
import { useDevices, useProvisionDevice, useDeprovisionDevice } from '../hooks/useDevices';
import { StatusBadge } from '../components/common/StatusBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function CustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>();
  const { data: customer, isLoading: customerLoading } = useCustomer(customerId!);
  const { data: devices, isLoading: devicesLoading } = useDevices(customerId);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [deviceToDeprovision, setDeviceToDeprovision] = useState<{
    device_id: string;
    device_name: string;
    assigned_ip: string;
  } | null>(null);

  if (customerLoading || devicesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Customer not found</p>
        <Link to="/customers" className="text-primary-600 hover:underline mt-2 block">
          Back to Customers
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <Link to="/customers" className="text-sm text-gray-500 hover:text-gray-700">
          ← Back to Customers
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{customer.organization_name}</h1>
          <p className="text-gray-500">{customer.billing_email}</p>
        </div>
        <StatusBadge status={customer.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Tier</h3>
          <p className="text-lg font-semibold text-gray-900 mt-1">{customer.tier}</p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Devices</h3>
          <p className="text-lg font-semibold text-gray-900 mt-1">
            {customer.device_count} / {customer.device_limit}
          </p>
        </div>
        <div className="card p-6">
          <h3 className="text-sm font-medium text-gray-500">Created</h3>
          <p className="text-lg font-semibold text-gray-900 mt-1">
            {new Date(customer.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Devices</h2>
          <button
            onClick={() => setShowProvisionModal(true)}
            className="btn-primary"
            disabled={customer.device_count >= customer.device_limit}
          >
            Provision Device
          </button>
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Device Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Assigned IP
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Activated
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {devices && devices.length > 0 ? (
              devices.map((device) => (
                <tr key={device.device_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {device.device_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {device.device_type === 'pi-gateway' ? 'Pi Gateway' : 'Windows Client'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono">
                    {device.assigned_ip}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={device.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                    {device.activated_at
                      ? new Date(device.activated_at).toLocaleDateString()
                      : 'Not activated'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {device.status !== 'cancelled' && (
                      <button
                        onClick={() =>
                          setDeviceToDeprovision({
                            device_id: device.device_id,
                            device_name: device.device_name,
                            assigned_ip: device.assigned_ip,
                          })
                        }
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        Deprovision
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  No devices provisioned yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showProvisionModal && (
        <ProvisionDeviceModal
          customerId={customerId!}
          onClose={() => setShowProvisionModal(false)}
        />
      )}

      {deviceToDeprovision && (
        <DeprovisionDeviceModal
          device={deviceToDeprovision}
          onClose={() => setDeviceToDeprovision(null)}
        />
      )}
    </div>
  );
}

function ProvisionDeviceModal({
  customerId,
  onClose,
}: {
  customerId: string;
  onClose: () => void;
}) {
  const provisionDevice = useProvisionDevice();
  const [deviceName, setDeviceName] = useState('');
  const [deviceType, setDeviceType] = useState<'windows-client' | 'pi-gateway'>('windows-client');
  const [piSerial, setPiSerial] = useState('');
  const [result, setResult] = useState<{
    activation_code: string;
    assigned_ip: string;
    device_type: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = await provisionDevice.mutateAsync({
        customer_id: customerId,
        device_name: deviceName,
        device_type: deviceType,
        ...(deviceType === 'pi-gateway' && piSerial ? { pi_serial: piSerial } : {}),
      });
      setResult({
        activation_code: data.activation_code,
        assigned_ip: data.assigned_ip,
        device_type: data.device_type,
      });
    } catch {
      // Error handled by react-query
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Device Provisioned</h2>

          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <p className="text-sm text-green-800">
              {result.device_type === 'pi-gateway'
                ? 'Pi gateway has been provisioned. It will auto-activate via IoT when powered on.'
                : 'Device has been provisioned successfully. Share the activation code with the user.'}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label">Assigned IP</label>
              <div className="font-mono text-lg bg-gray-100 p-3 rounded-md">
                {result.assigned_ip}
              </div>
            </div>

            <div>
              <label className="label">Activation Code</label>
              <div className="font-mono text-lg bg-gray-100 p-3 rounded-md break-all">
                {result.activation_code}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {result.device_type === 'pi-gateway'
                  ? 'The gateway will receive this code automatically via IoT'
                  : 'This code expires in 24 hours'}
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="btn-primary">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Provision Device</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Device Type</label>
            <select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value as 'windows-client' | 'pi-gateway')}
              className="input"
            >
              <option value="windows-client">Windows Client</option>
              <option value="pi-gateway">Pi Gateway</option>
            </select>
          </div>

          <div>
            <label className="label">Device Name</label>
            <input
              type="text"
              required
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="input"
              placeholder={
                deviceType === 'pi-gateway'
                  ? 'e.g., Field Office Gateway 1'
                  : 'e.g., Field Office Laptop 1'
              }
            />
          </div>

          {deviceType === 'pi-gateway' && (
            <div>
              <label className="label">Pi Serial Number</label>
              <input
                type="text"
                required
                value={piSerial}
                onChange={(e) => setPiSerial(e.target.value)}
                className="input"
                placeholder="e.g., 10000000abcdef01"
              />
              <p className="text-xs text-gray-500 mt-1">
                Found in /proc/cpuinfo on the Raspberry Pi
              </p>
            </div>
          )}

          {provisionDevice.isError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {provisionDevice.error?.message || 'Failed to provision device'}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={provisionDevice.isPending}
              className="btn-primary disabled:opacity-50"
            >
              {provisionDevice.isPending ? 'Provisioning...' : 'Provision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeprovisionDeviceModal({
  device,
  onClose,
}: {
  device: { device_id: string; device_name: string; assigned_ip: string };
  onClose: () => void;
}) {
  const deprovision = useDeprovisionDevice();

  const handleConfirm = async () => {
    try {
      await deprovision.mutateAsync(device.device_id);
      onClose();
    } catch {
      // Error handled by react-query
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Deprovision Device</h2>

        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-800">
            This will release the Elastic IP and revoke the device certificate.
            This action cannot be undone.
          </p>
        </div>

        <div className="space-y-2 mb-6">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Device:</span> {device.device_name}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">IP:</span>{' '}
            <span className="font-mono">{device.assigned_ip}</span>
          </p>
        </div>

        {deprovision.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
            {deprovision.error?.message || 'Failed to deprovision device'}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="btn-secondary" disabled={deprovision.isPending}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={deprovision.isPending}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {deprovision.isPending ? 'Deprovisioning...' : 'Deprovision'}
          </button>
        </div>
      </div>
    </div>
  );
}
